import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const ROLES = ['Admin', 'Compliance Manager', 'Manager', 'HR Admin', 'Team Lead', 'Viewer', 'Employee'];

const roleColors = {
  'Admin': 'bg-destructive/10 text-destructive border-destructive/20',
  'Compliance Manager': 'bg-primary/10 text-primary border-primary/20',
  'Manager': 'bg-warning/10 text-warning border-warning/20',
  'HR Admin': 'bg-accent/10 text-accent-foreground border-accent/20',
  'Team Lead': 'bg-secondary/10 text-secondary-foreground border-secondary/20',
  'Viewer': 'bg-muted text-muted-foreground border-border',
  'Employee': 'bg-muted text-muted-foreground border-border'
};

const UserManagement = () => {
  const navigate = useNavigate();
  const { userProfile, isAdmin, getAllUsers, inviteUser, updateUserRole, deactivateUser, reactivateUser, updateUserDetails, deleteUser } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Add user modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', fullName: '', role: 'Viewer' });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');
  const [createdCredentials, setCreatedCredentials] = useState(null);

  // Edit user modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ fullName: '', role: 'Viewer' });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  // Delete confirmation state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Role edit state (inline)
  const [editingRole, setEditingRole] = useState(null);
  const [roleLoading, setRoleLoading] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/compliance-overview');
      return;
    }
    loadUsers();
  }, [isAdmin]);

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    const { data, error: fetchError } = await getAllUsers();
    if (fetchError) {
      setError('Failed to load users. Please try again.');
    } else {
      setUsers(data || []);
    }
    setLoading(false);
  };

  const handleAddUser = async (e) => {
    e?.preventDefault();
    if (!addForm?.email?.trim() || !addForm?.fullName?.trim()) {
      setAddError('Email and full name are required.');
      return;
    }
    setAddLoading(true);
    setAddError('');

    const { data, error: inviteError, tempPassword } = await inviteUser(addForm?.email, addForm?.fullName, addForm?.role);

    if (inviteError) {
      setAddError(inviteError?.message || 'Failed to create user.');
      setAddLoading(false);
      return;
    }

    setCreatedCredentials({ email: addForm?.email, password: tempPassword });
    setAddForm({ email: '', fullName: '', role: 'Viewer' });
    setAddLoading(false);
    await loadUsers();
  };

  const handleOpenEdit = (user) => {
    setEditUser(user);
    setEditForm({ fullName: user?.full_name || '', role: user?.role || 'Viewer' });
    setEditError('');
    setShowEditModal(true);
  };

  const handleEditUser = async (e) => {
    e?.preventDefault();
    if (!editForm?.fullName?.trim()) {
      setEditError('Full name is required.');
      return;
    }
    setEditLoading(true);
    setEditError('');

    const { error: updateError } = await updateUserDetails(editUser?.id, {
      full_name: editForm?.fullName,
      role: editForm?.role,
    });

    if (updateError) {
      setEditError(updateError?.message || 'Failed to update user.');
      setEditLoading(false);
      return;
    }

    setUsers(prev => prev?.map(u => u?.id === editUser?.id ? { ...u, full_name: editForm?.fullName, role: editForm?.role } : u));
    setSuccessMsg('User updated successfully.');
    setTimeout(() => setSuccessMsg(''), 3000);
    setShowEditModal(false);
    setEditUser(null);
    setEditLoading(false);
  };

  const handleOpenDelete = (user) => {
    setDeleteTarget(user);
    setShowDeleteModal(true);
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    const { error: delError } = await deleteUser(deleteTarget?.id);
    if (delError) {
      setError(delError?.message || 'Failed to delete user.');
    } else {
      setUsers(prev => prev?.filter(u => u?.id !== deleteTarget?.id));
      setSuccessMsg(`User "${deleteTarget?.full_name || deleteTarget?.email}" deleted successfully.`);
      setTimeout(() => setSuccessMsg(''), 3000);
    }
    setDeleteLoading(false);
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const handleRoleChange = async (userId, newRole) => {
    setRoleLoading(userId);
    const { error: updateError } = await updateUserRole(userId, newRole);
    if (updateError) {
      setError('Failed to update role.');
    } else {
      setSuccessMsg('Role updated successfully.');
      setTimeout(() => setSuccessMsg(''), 3000);
      setUsers(prev => prev?.map(u => u?.id === userId ? { ...u, role: newRole } : u));
    }
    setEditingRole(null);
    setRoleLoading(null);
  };

  const handleToggleActive = async (user) => {
    const fn = user?.is_active ? deactivateUser : reactivateUser;
    const { error: toggleError } = await fn(user?.id);
    if (toggleError) {
      setError('Failed to update user status.');
    } else {
      setSuccessMsg(`User ${user?.is_active ? 'deactivated' : 'reactivated'} successfully.`);
      setTimeout(() => setSuccessMsg(''), 3000);
      setUsers(prev => prev?.map(u => u?.id === user?.id ? { ...u, is_active: !u?.is_active } : u));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground">User Management</h1>
              <p className="text-sm text-muted-foreground mt-1">Manage system users and their roles</p>
            </div>
            <Button
              iconName="UserPlus"
              onClick={() => { setShowAddModal(true); setCreatedCredentials(null); setAddError(''); }}
            >
              Add User
            </Button>
          </div>

          {/* Alerts */}
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20 flex items-center gap-2">
              <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0" />
              <p className="text-sm text-destructive">{error}</p>
              <button onClick={() => setError('')} className="ml-auto text-destructive hover:opacity-70">
                <Icon name="X" size={14} />
              </button>
            </div>
          )}
          {successMsg && (
            <div className="mb-4 p-3 rounded-lg bg-success/10 border border-success/20 flex items-center gap-2">
              <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0" />
              <p className="text-sm text-success">{successMsg}</p>
            </div>
          )}

          {/* Users Table */}
          <div className="bg-card rounded-xl elevation-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-border">
              <h2 className="text-base font-heading font-semibold text-foreground">
                System Users ({users?.length})
              </h2>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm text-muted-foreground">Loading users...</p>
                </div>
              </div>
            ) : users?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Icon name="Users" size={40} className="text-muted-foreground/40" />
                <p className="text-muted-foreground">No users found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">User</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Role</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Joined</th>
                      <th className="text-right px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {users?.map((u) => (
                      <tr key={u?.id} className="hover:bg-muted/20 transition-colors group">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-sm font-semibold text-primary">
                                {u?.full_name?.charAt(0)?.toUpperCase() || u?.email?.charAt(0)?.toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-foreground">{u?.full_name || '—'}</p>
                              <p className="text-xs text-muted-foreground">{u?.email}</p>
                            </div>
                            {u?.id === userProfile?.id && (
                              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">You</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {editingRole === u?.id ? (
                            <div className="flex items-center gap-2">
                              <select
                                className="text-xs border border-border rounded-lg px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                                defaultValue={u?.role}
                                onChange={(e) => handleRoleChange(u?.id, e?.target?.value)}
                                disabled={roleLoading === u?.id}
                              >
                                {ROLES?.map(r => (
                                  <option key={r} value={r}>{r}</option>
                                ))}
                              </select>
                              <button
                                onClick={() => setEditingRole(null)}
                                className="text-muted-foreground hover:text-foreground"
                              >
                                <Icon name="X" size={14} />
                              </button>
                            </div>
                          ) : (
                            <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${roleColors?.[u?.role] || roleColors?.['Viewer']}`}>
                              {u?.role}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border font-medium ${
                            u?.is_active
                              ? 'bg-success/10 text-success border-success/20' :'bg-muted text-muted-foreground border-border'
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${u?.is_active ? 'bg-success' : 'bg-muted-foreground'}`} />
                            {u?.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-xs text-muted-foreground">
                            {u?.created_at ? new Date(u?.created_at)?.toLocaleDateString() : '—'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-1">
                            {u?.id !== userProfile?.id && (
                              <>
                                <button
                                  onClick={() => handleOpenEdit(u)}
                                  className="p-1.5 rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                                  title="Edit user"
                                >
                                  <Icon name="Pencil" size={15} />
                                </button>
                                <button
                                  onClick={() => handleToggleActive(u)}
                                  className={`p-1.5 rounded-lg transition-colors ${
                                    u?.is_active
                                      ? 'hover:bg-warning/10 text-muted-foreground hover:text-warning'
                                      : 'hover:bg-success/10 text-muted-foreground hover:text-success'
                                  }`}
                                  title={u?.is_active ? 'Deactivate user' : 'Reactivate user'}
                                >
                                  <Icon name={u?.is_active ? 'UserX' : 'UserCheck'} size={15} />
                                </button>
                                <button
                                  onClick={() => handleOpenDelete(u)}
                                  className="p-1.5 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                                  title="Delete user"
                                >
                                  <Icon name="Trash2" size={15} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-modal bg-black/50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl elevation-lg w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h3 className="text-lg font-heading font-semibold text-foreground">Add New User</h3>
              <button
                onClick={() => { setShowAddModal(false); setCreatedCredentials(null); setAddError(''); }}
                className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="p-6">
              {createdCredentials ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-success">
                    <Icon name="CheckCircle" size={20} />
                    <p className="font-medium">User created successfully!</p>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/50 border border-border space-y-2">
                    <p className="text-sm font-medium text-foreground">Temporary Credentials:</p>
                    <p className="text-sm text-muted-foreground">Email: <span className="font-mono text-foreground">{createdCredentials?.email}</span></p>
                    <p className="text-sm text-muted-foreground">Password: <span className="font-mono text-foreground">{createdCredentials?.password}</span></p>
                    <p className="text-xs text-warning mt-2">⚠ Share these credentials securely. The user should change their password after first login.</p>
                  </div>
                  <Button
                    fullWidth
                    onClick={() => { setShowAddModal(false); setCreatedCredentials(null); }}
                  >
                    Done
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleAddUser} className="space-y-4">
                  {addError && (
                    <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                      <p className="text-sm text-destructive">{addError}</p>
                    </div>
                  )}

                  <Input
                    type="text"
                    name="fullName"
                    label="Full Name"
                    placeholder="Enter full name"
                    value={addForm?.fullName}
                    onChange={(e) => setAddForm(prev => ({ ...prev, fullName: e?.target?.value }))}
                    required
                    disabled={addLoading}
                  />

                  <Input
                    type="email"
                    name="email"
                    label="Email Address"
                    placeholder="Enter email address"
                    value={addForm?.email}
                    onChange={(e) => setAddForm(prev => ({ ...prev, email: e?.target?.value }))}
                    required
                    disabled={addLoading}
                  />

                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-foreground">Role</label>
                    <select
                      className="w-full border border-border rounded-lg px-3 py-2.5 bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      value={addForm?.role}
                      onChange={(e) => setAddForm(prev => ({ ...prev, role: e?.target?.value }))}
                      disabled={addLoading}
                    >
                      {ROLES?.map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                    <p className="text-xs text-muted-foreground">
                      Admin: Full access · Compliance Manager: Manage compliance · Manager: Team oversight · HR Admin: HR full access · Team Lead: Limited oversight · Viewer/Employee: Read-only
                    </p>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      fullWidth
                      onClick={() => { setShowAddModal(false); setAddError(''); }}
                      disabled={addLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      fullWidth
                      loading={addLoading}
                      iconName="UserPlus"
                    >
                      {addLoading ? 'Creating...' : 'Create User'}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editUser && (
        <div className="fixed inset-0 z-modal bg-black/50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl elevation-lg w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h3 className="text-lg font-heading font-semibold text-foreground">Edit User</h3>
              <button
                onClick={() => { setShowEditModal(false); setEditUser(null); setEditError(''); }}
                className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="p-6">
              <form onSubmit={handleEditUser} className="space-y-4">
                {editError && (
                  <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                    <p className="text-sm text-destructive">{editError}</p>
                  </div>
                )}

                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 border border-border">
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-semibold text-primary">
                      {editUser?.full_name?.charAt(0)?.toUpperCase() || editUser?.email?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Email (cannot be changed)</p>
                    <p className="text-sm font-medium text-foreground">{editUser?.email}</p>
                  </div>
                </div>

                <Input
                  type="text"
                  name="fullName"
                  label="Full Name"
                  placeholder="Enter full name"
                  value={editForm?.fullName}
                  onChange={(e) => setEditForm(prev => ({ ...prev, fullName: e?.target?.value }))}
                  required
                  disabled={editLoading}
                />

                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">Role</label>
                  <select
                    className="w-full border border-border rounded-lg px-3 py-2.5 bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    value={editForm?.role}
                    onChange={(e) => setEditForm(prev => ({ ...prev, role: e?.target?.value }))}
                    disabled={editLoading}
                  >
                    {ROLES?.map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    fullWidth
                    onClick={() => { setShowEditModal(false); setEditUser(null); setEditError(''); }}
                    disabled={editLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    fullWidth
                    loading={editLoading}
                    iconName="Save"
                  >
                    {editLoading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div className="fixed inset-0 z-modal bg-black/50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl elevation-lg w-full max-w-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h3 className="text-lg font-heading font-semibold text-foreground">Delete User</h3>
              <button
                onClick={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
                className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                disabled={deleteLoading}
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon name="Trash2" size={18} className="text-destructive" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Are you sure you want to delete this user?
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    <span className="font-medium text-foreground">{deleteTarget?.full_name || deleteTarget?.email}</span> will be permanently removed from the system. This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  fullWidth
                  onClick={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
                  disabled={deleteLoading}
                >
                  Cancel
                </Button>
                <button
                  onClick={handleDeleteUser}
                  disabled={deleteLoading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-destructive text-destructive-foreground text-sm font-medium hover:bg-destructive/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Icon name="Trash2" size={15} />
                      Delete User
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
